import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import JsonLd from '../components/JsonLd';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { LIMES_STATE, STATE_DATE, fmtInt } from '../lib/siteState';

// Public method page for LIMES: data, method, precision, limits. The numbers
// here come from docs/modules/limes (NOW.md, paper-notes.md, data-inventory.md)
// and must be refreshed there first, never here.

const SITE = 'https://tutela.land';

function Sub({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="font-display text-lg font-bold text-mist">{title}</h3>
      <div className="mt-2 space-y-3 leading-relaxed text-mist-muted">{children}</div>
    </div>
  );
}

const SOURCES: [string, string][] = [
  ['LiDAR nacional 2024 (DGT)', 'altura da vegetação por estrato, a base do estado de referência'],
  ['Ortofotomapas 2025, 25 cm (DGT)', 'cobertura de todo o continente, um ano depois do LiDAR'],
  ['Rede secundária de faixas (DGT, SGIFR)', 'a camada oficial, ingerida como está, nunca redesenhada'],
  ['Cadastro predial (INSPIRE WFS)', 'a parcela e o responsável, dados abertos com licença CC BY'],
  ['Sentinel-2 (Copernicus)', 'série semanal de mudança por unidade, com abstenção quando não há sinal'],
  ['Áreas ardidas (EFFIS) e dNBR próprio', 'para saber o que ardeu desde a referência'],
];

export default function LimesComoFunciona() {
  useDocumentMeta(
    'Como funciona o LIMES: dados, método e precisão | Tutela',
    'Como o LIMES lê as faixas de gestão de combustível: LiDAR nacional, ortofotomapas de 2025, cadastro e Sentinel-2, modelos de segmentação treinados em Portugal, precisão medida e limites declarados.',
    '/limes/como-funciona',
  );

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Tutela', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'LIMES', item: `${SITE}/limes` },
      { '@type': 'ListItem', position: 3, name: 'Como funciona', item: `${SITE}/limes/como-funciona` },
    ],
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <JsonLd id="limes-como-breadcrumb" data={breadcrumb} />
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          Módulo LIMES · Como funciona
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Dos dados abertos à lista de prioridades
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          Tudo o que o LIMES usa é público e tem licença que permite o uso municipal. O que
          acrescentamos é a leitura: modelos treinados sobre o próprio território português que
          transformam altura de vegetação e imagem numa ordem de trabalho com evidência.
        </p>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">Dados</div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Seis fontes, todas abertas
          </h2>
          <dl className="mt-8 space-y-2.5">
            {SOURCES.map(([k, v]) => (
              <div key={k} className="rounded-lg border border-ink-700 bg-ink-900 px-4 py-3">
                <dt className="font-mono text-[11px] uppercase tracking-wider text-ember">{k}</dt>
                <dd className="mt-1 text-sm text-mist-muted">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">Método</div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Referência, estado atual, mudança
          </h2>
          <div className="mt-8 space-y-8">
            <Sub title="1. A referência: o LiDAR de 2024">
              <p>
                Para cada faixa da rede secundária calculamos que fração carrega vegetação acima de 2 m,
                por estrato de altura. É o estado de referência e a métrica da lista: hectares de
                vegetação alta dentro da faixa, não uma opinião.
              </p>
            </Sub>
            <Sub title="2. O estado atual: os ortofotomapas de 2025">
              <p>
                Dois modelos de segmentação de imagem, treinados por nós com as etiquetas que o LiDAR
                dá de graça, leem os ortofotomapas de 2025 e estimam, unidade a unidade, o estado da
                faixa um ano depois. Um modelo estima o estado; o outro, mais exigente, levanta a
                bandeira de verificação. Nas faixas de teste de 2025 a exatidão binária (limpo ou não
                limpo) foi de 0,89.
              </p>
              <p>
                <span className="text-mist">Estes modelos foram treinados e medidos no território de
                Vila Franca de Xira.</span> Não vendemos um modelo nacional: cada município novo começa
                por uma prova sobre as suas próprias faixas, com os seus dados, e só depois dessa prova
                é que citamos um número para esse concelho.
              </p>
            </Sub>
            <Sub title="3. A regra que põe uma parcela na lista">
              <p>
                Uma situação a verificar exige vegetação densa em ambas as épocas e numa fração
                relevante da faixa. A regra foi dimensionada na distribuição real do concelho piloto:
                a versão ingénua sinalizava três em cada quatro unidades, o que não é uma lista, é uma
                parede vermelha. A regra que ficou sinalizou cerca de uma em cada cinco.
              </p>
            </Sub>
            <Sub title="4. O sinal semanal: Sentinel-2">
              <p>
                Todas as semanas o Sentinel-2 fornece uma diferença por unidade em relação ao verão
                anterior, com abstenção quando a faixa é demasiado estreita para o pixel. É um empurrão
                na ordenação e uma linha na ficha, nunca um veredicto: a resolução de 10 m não vê o que
                um técnico vê a pé.
              </p>
            </Sub>
          </div>
        </div>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">
            Precisão e limites
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            O que medimos e o que dizemos
          </h2>
          <div className="mt-8 space-y-8">
            <Sub title="A precisão que sustenta o vocabulário">
              <p>
                A precisão da sinalização situa-se entre 54% e 74% nas nossas medições, todas feitas em
                Vila Franca de Xira; noutro concelho o valor tem de ser medido de novo. É por isso que
                tudo no LIMES diz "situações a verificar" e nunca "incumprimento detetado". Uma lista com
                esta precisão vale muito para escolher onde ir; um auto com esta precisão seria um
                problema legal e o fim da confiança.
              </p>
            </Sub>
            <Sub title={`O estado da medição em ${STATE_DATE}`}>
              <p>
                Sobre Vila Franca de Xira: {fmtInt(LIMES_STATE.unitsEvaluated)} unidades avaliadas,{' '}
                {fmtInt(LIMES_STATE.verificar)} situações a verificar, {fmtInt(LIMES_STATE.reducao)} com redução
                clara entre 2024 e 2025, {LIMES_STATE.naoAvaliado} marcadas como "não avaliado". Estas últimas
                são uma lição: sobre o aeródromo de Alverca o ortofotomapa de 2025 está pixelizado pela DGT
                (blocos de cerca de 30 m) e o modelo leu os blocos lisos como terreno limpo com confiança
                alta. Encontrámos o erro ao rever os recortes à resolução nativa, e por isso qualquer unidade
                cuja imagem de 2025 seja grosseira fica fora da avaliação em vez de entrar na lista.
              </p>
              <p>
                Os {fmtInt(LIMES_STATE.review.total)} rótulos de mudança (redução ou aumento) passaram por uma
                revisão visual, recorte a recorte, à resolução nativa de 25 cm, feita por nós com apoio de
                IA e ainda não validada pelo município: {LIMES_STATE.review.confirma} confirmados,{' '}
                {LIMES_STATE.review.agricola} são colheita ou lavoura agrícola e não limpeza de faixa,{' '}
                {LIMES_STATE.review.duvida} em dúvida, {LIMES_STATE.review.naoConfirma} não confirmados,{' '}
                {LIMES_STATE.review.coarse} sobre imagem grosseira. Os rótulos agrícolas mostram o limite
                do sinal: a imagem não distingue uma faixa limpa de um campo colhido, e é a visita que decide.
              </p>
              <p>
                O que ainda não está medido: a precisão da deteção de mudança sobre pares controlados
                (sintéticos e 2018 para 2024) e a precisão no terreno, que só a confirmação do município dá.
              </p>
            </Sub>
            <Sub title="O que a imagem não distingue">
              <p>
                Pastagem de mato baixo, limpeza abaixo do limiar de deteção, regimes diferentes de uso
                do solo, sombra e época do voo. A classe "mato baixo" é a mais difícil para os modelos e
                está identificada como tal. A ficha mostra a imagem para que a pessoa decida.
              </p>
            </Sub>
            <Sub title="Cadência">
              <p>
                O estado de referência é anual (LiDAR, ortofotomapas); a mudança é semanal (Sentinel-2)
                e a rede oficial e o cadastro são ingeridos como instantâneos datados, com um portão que
                recusa um instantâneo com menos elementos do que o anterior. Uma fonte que morre em
                silêncio devolve zeros com ar de verdade; aprendemos isso com o IGNIS.
              </p>
            </Sub>
            <Sub title="Enquadramento legal">
              <p>
                A classificação oficial de perigosidade pertence ao ICNF e ao IPMA; os prazos de 30 e 60
                dias do decreto-lei 82/2021 contam sobre a carta oficial. O LIMES apoia o Gabinete Técnico
                Florestal a cumprir os seus deveres de monitorização e de registo anual; não classifica
                risco nem declara infrações.
              </p>
            </Sub>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-4 border-t border-ink-800 pt-8">
          <a
            href="mailto:hello@tutela.land?subject=LIMES"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Falar connosco
          </a>
          <Link
            to="/limes"
            className="inline-flex items-center gap-2 rounded-lg border border-ink-700 px-6 py-3 font-medium text-mist transition-colors hover:bg-ink-800"
          >
            O LIMES
          </Link>
          <Link
            to="/municipios"
            className="inline-flex items-center gap-2 rounded-lg px-5 py-3 font-mono text-sm uppercase tracking-widest text-mist-muted transition-colors hover:text-mist"
          >
            Para municípios
          </Link>
        </div>
      </div>
    </div>
  );
}
